import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { UserManager } from 'oidc-client-ts';
import type { ServiceTermsBlock } from '@quality-vet/contracts/service-terms';

import { ExpertGroundedAiPanel } from './expert-grounded-ai-panel';
import { Modal } from './modal';

const linkPattern = /((?:https?:\/\/|www\.)[^\s]+|[\w.+-]+@[\w.-]+\.[A-Za-z]{2,})/g;

interface TermsSection {
  heading?: Extract<ServiceTermsBlock, { type: 'heading' }>;
  blocks: ServiceTermsBlock[];
}

function linkedText(value: string): ReactNode {
  return value.split(linkPattern).map((part, index) => {
    if (!part) return null;
    if (/^[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}$/.test(part)) {
      return (
        <a className="text-link" href={`mailto:${part}`} key={`${part}-${index}`}>
          {part}
        </a>
      );
    }
    if (/^(?:https?:\/\/|www\.)/.test(part)) {
      const suffix = part.match(/[),.;:]+$/)?.[0] ?? '';
      const address = suffix ? part.slice(0, -suffix.length) : part;
      return (
        <span key={`${part}-${index}`}>
          <a
            className="text-link"
            href={address.startsWith('www.') ? `https://${address}` : address}
            rel="noreferrer"
            target="_blank"
          >
            {address}
          </a>
          {suffix}
        </span>
      );
    }
    return part;
  });
}

function partitionSections(blocks: readonly ServiceTermsBlock[]): TermsSection[] {
  const sections: TermsSection[] = [];
  let current: TermsSection = { blocks: [] };

  for (const block of blocks) {
    if (block.type === 'heading' && block.level === 2) {
      if (current.heading || current.blocks.length > 0) sections.push(current);
      current = { heading: block, blocks: [] };
    } else {
      current.blocks.push(block);
    }
  }
  if (current.heading || current.blocks.length > 0) sections.push(current);
  return sections;
}

function TermsBlockView({ block }: { block: ServiceTermsBlock }) {
  switch (block.type) {
    case 'heading':
      return block.level === 3 ? (
        <h4 className="app-legal-subheading">{linkedText(block.text)}</h4>
      ) : (
        <h5 className="app-legal-minor-heading">{linkedText(block.text)}</h5>
      );
    case 'paragraph':
      return <p>{linkedText(block.text)}</p>;
    case 'list':
      return (
        <ul>
          {block.items.map((item, index) => (
            <li key={`${item}-${index}`}>{linkedText(item)}</li>
          ))}
        </ul>
      );
    case 'table':
      return (
        <div
          aria-label="Scrollable Service Terms table"
          className="app-legal-table-scroll"
          role="region"
          tabIndex={0}
        >
          <table>
            <thead>
              <tr>
                {block.headers.map((header, index) => (
                  <th key={`${header}-${index}`} scope="col">
                    {linkedText(header)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowIndex) => (
                <tr key={`row-${rowIndex}`}>
                  {row.map((cell, cellIndex) => (
                    <td key={`cell-${rowIndex}-${cellIndex}`}>{linkedText(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
  }
}

interface ServiceTermsModule {
  readonly serviceTermsBlocks: readonly ServiceTermsBlock[];
  readonly serviceTermsEffectiveDate: string | null;
  readonly serviceTermsTitle: string;
}

async function currentAccessToken(): Promise<string> {
  const config = window.QUALITY_VET_CONFIG;
  const manager = new UserManager({
    authority: config.cognitoAuthority,
    client_id: config.cognitoClientId,
    redirect_uri: config.redirectUri,
    post_logout_redirect_uri: config.logoutUri,
    response_type: 'code',
    scope: 'openid email profile',
  });
  const user = await manager.getUser();
  if (!user?.access_token || user.expired) {
    throw new Error('Your session has expired. Log in again before sending a suggestion.');
  }
  return user.access_token;
}

function InfoIcon() {
  return (
    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="8.25" />
      <path d="M12 10.75v5.5" />
      <circle className="service-terms-trigger-dot" cx="12" cy="7.75" r="0.75" />
    </svg>
  );
}

export function ServiceTermsAction({
  expanded,
  onClick,
}: {
  expanded?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      aria-label="Service Terms"
      aria-expanded={expanded}
      aria-haspopup="dialog"
      className="service-terms-trigger"
      title="Service Terms"
      type="button"
      onClick={onClick}
    >
      <InfoIcon />
    </button>
  );
}

function FeatureIcon() {
  return (
    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
      <path d="M9 18h6M10 21h4M8.6 14.7C7.6 13.8 7 12.5 7 11a5 5 0 1 1 10 0c0 1.5-.6 2.8-1.6 3.7-.8.7-1.4 1.5-1.4 2.3H10c0-.8-.6-1.6-1.4-2.3Z" />
    </svg>
  );
}

export function ServiceTermsModal({ onClose, open }: { onClose: () => void; open: boolean }) {
  const [terms, setTerms] = useState<ServiceTermsModule>();
  const [termsError, setTermsError] = useState('');

  useEffect(() => {
    if (!open) return;
    if (terms) return;
    setTermsError('');
    let active = true;
    void import('@quality-vet/contracts/service-terms')
      .then((module) => {
        if (active) setTerms(module);
      })
      .catch(() => {
        if (active) setTermsError('The Privacy & Service Terms could not be loaded.');
      });
    return () => {
      active = false;
    };
  }, [open, terms]);

  const serviceTermsTitle = terms?.serviceTermsTitle ?? 'Privacy & Service Terms';
  const serviceTermsEffectiveDate = terms?.serviceTermsEffectiveDate;
  const termsSections = terms ? partitionSections(terms.serviceTermsBlocks) : [];

  return (
    <Modal active={open} fullScreen label={serviceTermsTitle} onClose={onClose}>
      <header className="app-modal-header">
        <p className="brand-name eyebrow">quality.vet</p>
        <h2>{serviceTermsTitle}</h2>
        {serviceTermsEffectiveDate ? (
          <p className="app-modal-effective">Effective {serviceTermsEffectiveDate}</p>
        ) : null}
      </header>
      <div className="app-modal-content">
        {!terms && !termsError ? (
          <p aria-atomic="true" role="status" aria-live="polite">
            Loading Privacy &amp; Service Terms…
          </p>
        ) : null}
        {termsError ? (
          <p className="auth-error" role="alert">
            {termsError}
          </p>
        ) : null}
        {termsSections.map((section, index) => {
          const sectionKey = `${section.heading?.text ?? 'introduction'}-${index}`;
          const sectionContent = (
            <section className="app-legal-section" key={sectionKey}>
              {section.heading ? <h3>{linkedText(section.heading.text)}</h3> : null}
              {section.blocks.map((block, blockIndex) => (
                <TermsBlockView block={block} key={`${sectionKey}-${blockIndex}`} />
              ))}
            </section>
          );
          return section.heading && /^general$/i.test(section.heading.text.trim()) ? (
            <div className="service-terms-general-layout" key={`general-${sectionKey}`}>
              {sectionContent}
              <ExpertGroundedAiPanel />
            </div>
          ) : (
            sectionContent
          );
        })}
      </div>
    </Modal>
  );
}

function SuggestFeatureModal({
  onClose,
  onSubmitted,
  submitterName,
}: {
  onClose: () => void;
  onSubmitted: () => void;
  submitterName: string | undefined;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');

  const submitSuggestion = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPending(true);
    setError('');
    try {
      const accessToken = await currentAccessToken();
      const response = await fetch(`${window.QUALITY_VET_CONFIG.apiUrl}/v1/feature-suggestions`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${accessToken}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim(),
          ...(submitterName?.trim() ? { submitterName: submitterName.trim() } : {}),
        }),
      });
      if (!response.ok) throw new Error(`Feature suggestion HTTP ${response.status}`);
      onSubmitted();
    } catch (submitError) {
      setError(
        submitError instanceof Error && submitError.message.startsWith('Your session has expired')
          ? submitError.message
          : 'Your suggestion could not be sent. Please try again.',
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <Modal label="Suggest a Feature" onClose={onClose}>
      <header className="app-modal-header">
        <p className="brand-name eyebrow">quality.vet</p>
        <h2>Suggest a Feature</h2>
        <p className="brand-copy">
          Help us improve quality.vet. Your suggestion will be sent to info@quality.vet.
        </p>
      </header>
      <form className="feature-suggestion-form" onSubmit={(event) => void submitSuggestion(event)}>
        <label>
          Feature title
          <input
            autoFocus
            maxLength={200}
            onChange={(event) => {
              setTitle(event.target.value);
              setError('');
            }}
            placeholder="What would make quality.vet better?"
            value={title}
          />
        </label>
        <label>
          Description
          <textarea
            maxLength={2_000}
            onChange={(event) => {
              setDescription(event.target.value);
              setError('');
            }}
            placeholder="Describe the problem, who it helps and how you expect it to work."
            required
            value={description}
          />
        </label>
        {error ? (
          <p className="auth-error" role="alert">
            {error}
          </p>
        ) : null}
        <div className="feature-suggestion-actions">
          <button className="feature-suggestion-submit" disabled={pending} type="submit">
            {pending ? 'Sending…' : 'Send suggestion'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function FloatingActions({ submitterName }: { submitterName?: string }) {
  const [modal, setModal] = useState<'terms' | 'feature' | null>(null);
  const [toast, setToast] = useState('');

  useEffect(() => {
    if (!toast) return;
    const timeout = window.setTimeout(() => setToast(''), 5000);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  const handleSuggestionSubmitted = () => {
    setModal(null);
    setToast('Thank you. Your suggestion has been sent to info@quality.vet.');
  };

  return (
    <>
      <aside aria-label="Help and feedback" className="floating-actions">
        <button
          aria-label="Suggest a Feature"
          title="Suggest a Feature"
          type="button"
          onClick={() => setModal('feature')}
        >
          <FeatureIcon />
        </button>
        <ServiceTermsAction expanded={modal === 'terms'} onClick={() => setModal('terms')} />
      </aside>
      <ServiceTermsModal onClose={() => setModal(null)} open={modal === 'terms'} />
      {modal === 'feature' ? (
        <SuggestFeatureModal
          onClose={() => setModal(null)}
          onSubmitted={handleSuggestionSubmitted}
          submitterName={submitterName}
        />
      ) : null}
      {toast ? (
        <p aria-atomic="true" aria-live="polite" className="floating-toast" role="status">
          {toast}
        </p>
      ) : null}
    </>
  );
}
